import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  TextField,
  InputAdornment,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Quiz as QuizIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  TrendingUp as ScoreIcon,
  AccessTime as DurationIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuiz } from '../../contexts/QuizContext';
import quizService, { QuizAttempt, UserQuizAttempts } from '../../services/quizService';
import ContentRenderer from '../../components/common/ContentRenderer';

const QuizHistory: React.FC = () => {
  const navigate = useNavigate();
  const { clearTakingState } = useQuiz();
  // const { } = useAuth(); // Removed as not needed

  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAttempts, setFilteredAttempts] = useState<QuizAttempt[]>([]);

  useEffect(() => {
    loadUserAttempts();
    clearTakingState(); // Clear quiz context state on mount
  }, [currentPage, clearTakingState]);

  useEffect(() => {
    // Filter attempts based on search term
    if (searchTerm.trim() === '') {
      setFilteredAttempts(attempts);
    } else {
      const filtered = attempts.filter(attempt =>
        attempt.quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (attempt.quiz.description && attempt.quiz.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredAttempts(filtered);
    }
  }, [attempts, searchTerm]);

  const loadUserAttempts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response: UserQuizAttempts = await quizService.getUserAttempts(currentPage, {
        per_page: 12,
      });

      setAttempts(response.attempts);
      setTotalPages(response.last_page);
    } catch (err: any) {
      console.error('Error loading user attempts:', err);
      setError(err.message || 'Không thể tải lịch sử bài tập');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleViewResults = (attempt: QuizAttempt) => {
    const { id, quiz_id, quiz, submitted_at } = attempt;

    if (attempt.quiz.show_results && attempt.submitted_at && attempt.id && attempt.quiz_id && !isNaN(Number(attempt.id)) && !isNaN(Number(attempt.quiz_id))) {
      navigate(`/quiz/${attempt.quiz_id}/results/${attempt.id}`);
    } else {
      console.warn('Không thể xem kết quả: thiếu dữ liệu cần thiết', attempt);
    }
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'Không xác định';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScoreColor = (percentage: string | number | null): 'success' | 'warning' | 'error' | 'default' => {
    if (percentage === null || percentage === undefined) return 'default';
    const numPercentage = Number(percentage);
    if (isNaN(numPercentage)) return 'default';
    if (numPercentage >= 80) return 'success';
    if (numPercentage >= 60) return 'warning';
    return 'error';
  };

  const getStatusChip = (attempt: QuizAttempt) => {
    if (!attempt.submitted_at) {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Chưa hoàn thành"
          color="error"
          variant="outlined"
          size="small"
          sx={{
            pointerEvents: 'none', // Ngăn click
            userSelect: 'none' // Ngăn select text
          }}
        />
      );
    }

    return (
      <Chip
        icon={<CheckIcon />}
        label="Đã hoàn thành"
        color="success"
        variant="outlined"
        size="small"
        sx={{
          pointerEvents: 'none', // Ngăn click
          userSelect: 'none' // Ngăn select text
        }}
      />
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Đang tải lịch sử bài tập...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={loadUserAttempts}
          >
            Thử lại
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <QuizIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h4" component="h1">
                Lịch sử bài tập
              </Typography>
            </Box>
            <Chip
              label={`${attempts.length} bài tập đã làm`}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Typography variant="body1" color="text.secondary">
            Xem lại các bài tập bạn đã hoàn thành và kết quả đạt được
          </Typography>
        </Paper>

        {/* Search */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Tìm kiếm bài tập theo tên hoặc mô tả..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 500 }}
          />
        </Box>

        {/* Results Count */}
        {searchTerm && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Tìm thấy {filteredAttempts.length} kết quả cho "{searchTerm}"
            </Typography>
          </Box>
        )}

        {/* Quiz Attempts Grid */}
        {filteredAttempts.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <QuizIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'Không tìm thấy bài tập nào' : 'Bạn chưa làm bài tập nào'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {searchTerm
                ? 'Thử tìm kiếm với từ khóa khác'
                : 'Hãy bắt đầu làm bài tập để xem lịch sử tại đây'
              }
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                onClick={() => navigate('/quizzes')}
              >
                Xem danh sách bài tập
              </Button>
            )}
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {filteredAttempts.map((attempt) => (
              <Box key={attempt.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Quiz Title */}
                    <Typography variant="h6" component="h3" gutterBottom>
                      {attempt.quiz.title}
                    </Typography>

                    {/* Quiz Description */}
                    {attempt.quiz.description && (
                      <Box sx={{ mb: 2 }}>
                        <ContentRenderer
                          text={attempt.quiz.description}
                          display="block"
                        />
                      </Box>
                    )}

                    {/* Status */}
                    <Box mb={2}>
                      {getStatusChip(attempt)}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Attempt Details */}
                    <Box>
                      {/* Date */}
                      <Box display="flex" alignItems="center" mb={1}>
                        <DateIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(attempt.started_at)}
                        </Typography>
                      </Box>

                      {/* Duration */}
                      {attempt.time_taken && (
                        <Box display="flex" alignItems="center" mb={1}>
                          <DurationIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Thời gian: {formatDuration(attempt.time_taken)}
                          </Typography>
                        </Box>
                      )}

                      {/* Score */}
                      {attempt.percentage !== null && attempt.percentage !== undefined && !isNaN(Number(attempt.percentage)) && (
                        <Box display="flex" alignItems="center" mb={1}>
                          <ScoreIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Điểm số:
                          </Typography>
                          <Chip
                            label={`${Number(attempt.percentage).toFixed(1)}%`}
                            color={getScoreColor(attempt.percentage)}
                            size="small"
                            sx={{
                              ml: 1,
                              pointerEvents: 'none', // Ngăn click
                              userSelect: 'none' // Ngăn select text
                            }}
                          />
                        </Box>
                      )}

                      {/* Question Count */}
                      <Box display="flex" alignItems="center">
                        <QuizIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {attempt.actual_question_count} câu hỏi
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    {attempt.quiz.show_results && attempt.submitted_at ? (
                      <Button
                        variant="contained"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewResults(attempt)}
                        fullWidth
                        disabled={!attempt.id || !attempt.quiz_id} // Disable nếu thiếu dữ liệu quan trọng
                      >
                        Xem kết quả
                      </Button>
                    ) : (
                      <Tooltip title="Kết quả không khả dụng">
                        <span style={{ width: '100%' }}>
                          <Button
                            variant="outlined"
                            disabled
                            fullWidth
                            sx={{
                              pointerEvents: 'none', // Ngăn click hoàn toàn
                              userSelect: 'none'
                            }}
                          >
                            Không có kết quả
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {/* Pagination */}
        {!searchTerm && totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default QuizHistory; 